import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header/Header';
import servicesDetailData from '../data/servicesDetailData';
import './ProjectDetail.css';

const projectsData = [
  {
    id: 1,
    slug: "modern-villa-electrical",
    name: "Modern Villa Electrical System",
    type: "Residential Installation",
    category: "Residential",
    client: "Mr. & Mrs. Kamau",
    location: "Karen, Nairobi",
    duration: "3 Months",
    year: "2025",
    featuredImage: "/images/projects/project-1.jpg",
    images: [
      "/images/projects/detail/villa-1.jpg",
      "/images/projects/detail/villa-2.jpg",
      "/images/projects/detail/villa-3.jpg",
      "/images/projects/detail/villa-4.jpg"
    ],
    overview: "Complete electrical design and installation for a luxury 5-bedroom villa with smart home integration, automated lighting, and energy management systems. The project involved full wiring, distribution board setup, backup generator integration, and smart home automation.",
    challenge: "The main challenge was integrating the smart home system with the existing architectural design while maintaining the aesthetic appeal of the luxury villa. All wiring had to be concealed within walls and custom fixtures.",
    solution: "We designed a comprehensive electrical plan that included hidden conduits, strategic outlet placement, and a centralized control system. Smart panels were installed in utility areas while maintaining seamless control through mobile apps and wall-mounted touchscreens.",
    results: "The project was completed on time and within budget. The homeowners now enjoy a fully automated home with energy monitoring, remote-controlled lighting, automated blinds, and a state-of-the-art security system integrated with their electrical setup."
  },
  {
    id: 2,
    slug: "commercial-solar-farm",
    name: "Commercial Solar Farm Setup",
    type: "Solar Systems",
    category: "Commercial",
    client: "Green Energy Ltd",
    location: "Industrial Area, Mombasa",
    duration: "4 Months",
    year: "2025",
    featuredImage: "/images/projects/project-2.jpg",
    images: [
      "/images/projects/detail/solar-1.jpg",
      "/images/projects/detail/solar-2.jpg",
      "/images/projects/detail/solar-3.jpg",
      "/images/projects/detail/solar-4.jpg"
    ],
    overview: "50kW solar panel installation for commercial building with battery storage, inverter systems, and cloud-based monitoring platform. The project included 200 solar panels, 4 inverters, and a 100kWh battery storage system.",
    challenge: "The roof structure required reinforcement to support the additional weight of the solar panels. Additionally, the building's electrical system needed upgrades to handle the bidirectional power flow.",
    solution: "We worked with structural engineers to reinforce the roof and designed a modular mounting system. The electrical upgrade included new distribution panels, bi-directional meters, and an automatic transfer switch for seamless grid integration.",
    results: "The client now generates 60% of their electricity needs from solar, reducing their power bills by approximately KES 150,000 per month."
  },
  {
    id: 3,
    slug: "factory-cctv-security",
    name: "Factory CCTV & Security Network",
    type: "Security Systems",
    category: "Industrial",
    client: "East African Industries",
    location: "Athi River, Machakos",
    duration: "2 Months",
    year: "2025",
    featuredImage: "/images/projects/project-3.jpg",
    images: [
      "/images/projects/detail/cctv-1.jpg",
      "/images/projects/detail/cctv-2.jpg",
      "/images/projects/detail/cctv-3.jpg",
      "/images/projects/detail/cctv-4.jpg"
    ],
    overview: "Comprehensive CCTV and alarm system with 64 cameras, motion detection, remote access, and 24/7 monitoring for industrial manufacturing facility spanning 5 acres.",
    challenge: "The vast area required strategic camera placement to ensure zero blind spots. Network infrastructure had to support high-bandwidth video streaming across the facility.",
    solution: "We implemented a fiber optic backbone for the camera network, installed PTZ cameras at key junctions, and fixed cameras at entry/exit points. The NVR system supports 90-day recording with AI-powered motion detection.",
    results: "Security incidents reduced by 85% within the first quarter. The management now has real-time visibility of the entire facility from any device."
  }
];

const recentPosts = [
  { title: "5 Signs Your Home Needs Electrical Rewiring", date: "April 15, 2026" },
  { title: "Benefits of Installing Solar Panels in Kenya", date: "April 10, 2026" },
  { title: "Choosing the Right CCTV System", date: "April 5, 2026" },
  { title: "Electrical Safety Tips Every Homeowner Should Know", date: "March 28, 2026" }
];

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = projectsData.find(p => p.slug === slug);
  const [lightboxImage, setLightboxImage] = useState(null);

  if (!project) {
    return (
      <div className="page-wrapper">
        <Header />
        <div className="not-found"><h1>Project Not Found</h1><Link to="/projects">Back to Projects</Link></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="hero-wrapper">
        <Header />
        <div className="page-hero" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + project.featuredImage})` }}>
          <div className="page-hero-overlay" />
          <div className="page-hero-content">
            <h1>{project.name}</h1>
            <div className="breadcrumb">
              <a href="/">Home</a><span className="sep">/</span>
              <a href="/projects">Projects</a><span className="sep">/</span>
              <span className="active">{project.name}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="project-detail-container">
        <div className="project-detail-row">
          <div className="project-main">
            {/* Featured Image with Category Tag on Top Left */}
            <div className="featured-image" onClick={() => setLightboxImage(project.featuredImage)}>
              <span className="featured-tag">{project.type}</span>
              <img src={process.env.PUBLIC_URL + project.featuredImage} alt={project.name} />
              <div className="image-overlay"><span>🔍 Click to enlarge</span></div>
            </div>

            <div className="project-gallery">
              {project.images.map((img, i) => (
                <div key={i} className="gallery-img" onClick={() => setLightboxImage(img)}>
                  <img src={process.env.PUBLIC_URL + img} alt={`${project.name} ${i+1}`} />
                  <div className="gallery-overlay"><span>🔍</span></div>
                </div>
              ))}
            </div>

            <div className="project-info-bar">
              <div className="info-item"><strong>Client</strong><span>{project.client}</span></div>
              <div className="info-item"><strong>Location</strong><span>{project.location}</span></div>
              <div className="info-item"><strong>Duration</strong><span>{project.duration}</span></div>
              <div className="info-item"><strong>Year</strong><span>{project.year}</span></div>
            </div>

            <div className="project-section"><h2>Project Overview</h2><p>{project.overview}</p></div>
            <div className="project-section"><h2>The Challenge</h2><p>{project.challenge}</p></div>
            <div className="project-section"><h2>Our Solution</h2><p>{project.solution}</p></div>
            <div className="project-section"><h2>Results & Impact</h2><p>{project.results}</p></div>

            <div className="project-cta-bottom">
              <h3>Interested in a similar project?</h3>
              <p>Let's discuss your requirements and bring your vision to life.</p>
              <a href="tel:0796049254" className="cta-btn">Call Us Now</a>
            </div>
          </div>

          <div className="project-sidebar">
            <div className="sidebar-card">
              <h4>Our Services</h4>
              <ul>{servicesDetailData.slice(0, 6).map(s => (<li key={s.id}><Link to={`/services/${s.slug}`}>{s.name}</Link></li>))}</ul>
            </div>
            <div className="sidebar-card">
              <h4>Recent Posts</h4>
              <ul className="posts-list">{recentPosts.map((post, i) => (<li key={i}><Link to="/blog">{post.title}</Link><span className="post-date">{post.date}</span></li>))}</ul>
            </div>
            <div className="sidebar-cta">
              <h4>Interested in this project?</h4>
              <p>Call us now for a free consultation</p>
              <a href="tel:0796049254" className="sidebar-cta-btn">📞 0796049254</a>
            </div>
          </div>
        </div>
      </div>

      {lightboxImage && (
        <div className="lightbox" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)}>✕</button>
          <img src={process.env.PUBLIC_URL + lightboxImage} alt="Project" className="lightbox-image" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
